/*
 * Hearth & Oak review carousel (sections/ho-review-carousel.liquid)
 *
 * - The viewport is a normal horizontally scrollable element, so swiping, trackpads and the
 *   keyboard (arrow keys while it has focus) work natively.
 * - Autoplay moves it slowly right to left. The cards are duplicated once (hidden from
 *   assistive technology) so the loop wraps without a visible jump.
 * - Pauses while hovered, focused or touched, while off screen and in background tabs.
 *   No autoplay when the shopper prefers reduced motion.
 */
(function () {
  'use strict';

  if (window.HOCarousel) {
    window.HOCarousel.initAll();
    return;
  }

  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');

  function Carousel(el) {
    this.el = el;
    this.viewport = el.querySelector('[data-ho-carousel-viewport]');
    this.track = el.querySelector('[data-ho-carousel-track]');
    this.prev = el.querySelector('[data-ho-carousel-prev]');
    this.next = el.querySelector('[data-ho-carousel-next]');
    this.pauseBtn = el.querySelector('[data-ho-carousel-pause]');
    this.speed = parseFloat(el.dataset.speed) || 25; // px per second
    this.autoplay = el.dataset.autoplay === 'true' && !(reduceMotion && reduceMotion.matches);
    this.holds = {}; // reasons the motion is paused: hover, focus, touch, offscreen, user
    this.pos = 0;
    this.last = null;
    this.frame = this.frame.bind(this);

    if (this.autoplay) this.cloneCards();
    this.bind();
    if (this.autoplay) {
      this.pauseBtn.hidden = false;
      requestAnimationFrame(this.frame);
    }
  }

  Carousel.prototype = {
    cloneCards: function () {
      var originals = Array.prototype.slice.call(this.track.children);
      // Repeat until one set is wider than the viewport, so the loop always has cards in view.
      var copies = Math.max(1, Math.ceil(this.viewport.clientWidth * 2 / Math.max(1, this.track.scrollWidth)));
      for (var c = 0; c < copies; c++) {
        originals.forEach(function (card) {
          var clone = card.cloneNode(true);
          clone.setAttribute('aria-hidden', 'true');
          clone.setAttribute('data-ho-clone', '');
          clone.removeAttribute('data-shopify-editor-block');
          clone.querySelectorAll('a, button, [tabindex]').forEach(function (n) { n.setAttribute('tabindex', '-1'); });
          this.track.appendChild(clone);
        }, this);
      }
      this.setWidth = null; // measured lazily (images may still be loading)
    },

    // Width of one full set of original cards, including the gap after the last one.
    loopWidth: function () {
      var cards = this.track.children;
      var originals = Array.prototype.filter.call(cards, function (c) { return !c.hasAttribute('data-ho-clone'); });
      var firstClone = this.track.querySelector('[data-ho-clone]');
      if (!firstClone || !originals.length) return 0;
      return firstClone.offsetLeft - originals[0].offsetLeft;
    },

    hold: function (reason, on) {
      if (on) this.holds[reason] = true; else delete this.holds[reason];
      if (!this.isHeld()) this.last = null; // resume smoothly without a jump
    },

    isHeld: function () {
      return Object.keys(this.holds).length > 0 || document.hidden;
    },

    frame: function (now) {
      requestAnimationFrame(this.frame);
      if (this.isHeld()) { this.last = null; return; }
      if (this.last === null) {
        this.last = now;
        this.pos = this.viewport.scrollLeft;
        return;
      }
      // Keep real elapsed time so the speed holds on slow devices; pauses and tab switches reset the clock.
      var dt = Math.min(500, now - this.last) / 1000;
      this.last = now;
      this.pos += this.speed * dt;
      var loop = this.loopWidth();
      if (loop > 0 && this.pos >= loop) this.pos -= loop;
      this.viewport.scrollLeft = this.pos;
    },

    step: function (direction) {
      var card = this.track.children[0];
      var gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      var distance = card ? card.getBoundingClientRect().width + gap : this.viewport.clientWidth * 0.8;
      var target = this.viewport.scrollLeft + direction * distance;
      var loop = this.loopWidth();
      // Going back from the start: jump forward one set first so there is room to move.
      if (loop > 0 && target < 0) {
        this.viewport.scrollLeft += loop;
        target += loop;
      }
      this.hold('button', true);
      this.viewport.scrollTo({ left: target, behavior: reduceMotion && reduceMotion.matches ? 'auto' : 'smooth' });
      var self = this;
      clearTimeout(this.buttonTimer);
      this.buttonTimer = setTimeout(function () { self.hold('button', false); }, 2500);
    },

    bind: function () {
      var self = this;
      var vp = this.viewport;

      this.prev.addEventListener('click', function () { self.step(-1); });
      this.next.addEventListener('click', function () { self.step(1); });

      this.pauseBtn.addEventListener('click', function () {
        var paused = !self.holds.user;
        self.hold('user', paused);
        self.pauseBtn.setAttribute('aria-pressed', paused ? 'true' : 'false');
        self.pauseBtn.setAttribute('aria-label', paused ? 'Resume scrolling' : 'Pause scrolling');
        self.el.classList.toggle('is-paused', paused);
      });

      this.el.addEventListener('mouseenter', function () { self.hold('hover', true); });
      this.el.addEventListener('mouseleave', function () { self.hold('hover', false); });
      this.el.addEventListener('focusin', function () { self.hold('focus', true); });
      this.el.addEventListener('focusout', function (evt) {
        if (!self.el.contains(evt.relatedTarget)) self.hold('focus', false);
      });

      // Touch: stop while the finger is down, resume a little after it lifts.
      vp.addEventListener('touchstart', function () {
        clearTimeout(self.touchTimer);
        self.hold('touch', true);
      }, { passive: true });
      vp.addEventListener('touchend', function () {
        clearTimeout(self.touchTimer);
        self.touchTimer = setTimeout(function () { self.hold('touch', false); }, 3000);
      }, { passive: true });

      // Keep manual scrolling inside the loop so it can carry on seamlessly.
      vp.addEventListener('scroll', function () {
        if (!self.autoplay) return;
        var loop = self.loopWidth();
        if (loop > 0 && vp.scrollLeft >= loop * 2) vp.scrollLeft -= loop;
        if (self.isHeld()) self.pos = vp.scrollLeft;
      }, { passive: true });

      if ('IntersectionObserver' in window) {
        new IntersectionObserver(function (entries) {
          self.hold('offscreen', !entries[0].isIntersecting);
        }).observe(this.el);
      }

      document.addEventListener('visibilitychange', function () { self.last = null; });
    }
  };

  window.HOCarousel = {
    initAll: function () {
      document.querySelectorAll('[data-ho-carousel]:not([data-ho-ready])').forEach(function (el) {
        el.setAttribute('data-ho-ready', '');
        try { new Carousel(el); } catch (e) { if (window.console) console.error('[Hearth & Oak carousel]', e); }
      });
    }
  };

  window.HOCarousel.initAll();
  document.addEventListener('DOMContentLoaded', window.HOCarousel.initAll);
  document.addEventListener('shopify:section:load', window.HOCarousel.initAll);
})();
